import { useQuery } from '@tanstack/react-query'
import api from '../api'

// ---- Helpers ---------------------------------------------------------------

// Build params safely: drop undefined/empty values
const buildParams = (p = {}) => {
  const params = {
    search: p.search?.trim(),
    category_id: p.category_id ?? undefined,
    brand_id: p.brand_id ?? undefined,
    min_price: p.min_price ?? undefined,
    max_price: p.max_price ?? undefined,
    on_sale: p.on_sale ?? undefined,
    sort_by: p.sort_by ?? 'created_at',
    sort_order: p.sort_order ?? 'desc',
    paginate: typeof p.paginate === 'boolean' ? p.paginate : true,
    per_page: p.per_page ?? 15,
    page: p.page ?? 1,
  }
  // Remove keys with undefined or empty strings
  return Object.fromEntries(
    Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== ''
    )
  )
}

// Normalize your controller's two shapes:
// 1) Non-paginated: { success, data: Product[], message }
// 2) Paginated:     { success, data: { data: Product[], ...meta }, message }
const normalizeResponse = (payload) => {
  const ok = !!payload?.success
  const msg = payload?.message ?? ''
  const raw = payload?.data

  if (Array.isArray(raw)) {
    return { success: ok, items: raw, meta: null, message: msg }
  }

  if (raw && Array.isArray(raw.data)) {
    const { data: items, ...meta } = raw
    return { success: ok, items, meta, message: msg }
  }

  // Fallback
  return { success: ok, items: [], meta: null, message: msg }
}

// ---- Fetcher ---------------------------------------------------------------
const fetchProducts = async (params) => {
  const res = await api.get('/products/all', { params: buildParams(params) })
  return normalizeResponse(res.data)
}

// ---- Hook ------------------------------------------------------------------
export const useProducts = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['products', buildParams(params)],
    queryFn: () => fetchProducts(params),
    // Smooth pagination / filters UX
    keepPreviousData: true,
    staleTime: 60_000,
    ...options,
  })
}
